import { useEffect, useState, useMemo } from 'react';
import { t } from 'utilities/TranslationUtils';
import { observer } from 'mobx-react-lite';
import { TableWithFilters } from 'components/Tables/TableWithFilters';
import { useHistory, useParams } from 'react-router-dom';

// components
import { AppConfirmDialog, AppCopyText, AppPageHeader, AppSectionErrorBoundary, AppUseIcon, AppUserTypeChip } from 'components/ui';
import { TableCell } from '@trackman/web-shared-components';
import { AppButton } from 'components/form';
import { useStores } from 'index';
import { SingleUserType, UserTypes } from 'types/GlobalTypes';
import { AddUserDialog } from './AddUserDialog';
import { usersTableColumns, appTableFiltersInstance } from './UsersTableColumns';

// types
import { IOrganization } from 'types/models/OrganizationModels';

export const UsersPage = observer(() => {
  const history = useHistory();
  const { apiServiceAxiosInstance: apiInstance, organizationsStore, usersStore, roleStore, analyticsStore } = useStores();
  const [isConfirmRevokeDialogOpen, setIsConfirmRevokeDialogOpen] = useState(false);
  const [userToRevoke, setUserToRevoke] = useState<SingleUserType | null>(null);

  const { organizationId } = useParams<{ organizationId: string }>();
  const [organizationName, setOrganizationName] = useState<string>();

  useEffect(() => {
    organizationsStore.getOrganizations(apiInstance);
    roleStore.getRoles(apiInstance);
    usersStore.getUsers(apiInstance, organizationId);
    analyticsStore.sendPageView('UsersPage');
  }, []);

  const rows = useMemo(() => usersStore.users, [usersStore.users]);

  const columns = useMemo(() => usersTableColumns(usersStore.users), [usersStore.users]);

  useEffect(() => {
    getOrganizationName();
  }, [organizationId]);

  const getOrganizationName = async () => {
    const orgName = await organizationsStore.getOrganizationName(apiInstance, organizationId);
    setOrganizationName(orgName);
  };

  const handleRevokingUserAccess = async (user) => {
    try {
      const response = await usersStore.RevokeUser(apiInstance, { userId: user.id, organizationId: organizationId });
      if (response) {
        usersStore.getUsers(apiInstance, organizationId);
      } else {
        console.log('error', response);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const renderCell = ({ cellValue, columnHeader, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {
    if (columnId === 'userId' || columnId === 'email') {
      return (
        <div className='d-flex justify-content-between align-items-center w-100'>
          <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
            {cellValue}
          </p>
          <AppCopyText className='ml-1 ' text={cellValue} />
        </div>
      );
    }

    if (columnId === 'type') {
      return <AppUserTypeChip userType={cellValue as UserTypes} />;
    }

    if (columnId === 'actions') {
      return (
        <>
          {
            <AppButton
              variant='secondary'
              className='border-secondary'
              data-test='revoke-user-button'
              onClick={(e) => {
                setUserToRevoke(row);
                setIsConfirmRevokeDialogOpen(true);
              }}
              disabled={usersStore.isRevokingAccess}
            >
              {t('revoke')}
              <AppUseIcon name='minusWithRoundedBorder' width='16' height='16' className='ml-2' color='#A0A0A0' />
            </AppButton>
          }
        </>
      );
    }

    return cellValue || '-';
  };

  const rowMapper = (cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell {...getCellProps()} className='table-body-cell' align={column.align} onClick={() => { }}>
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
          </TableCell>
        ))}
      </>
    );
  };

  return (
    <AppSectionErrorBoundary>
      <div>
        <AppPageHeader
          title={organizationName ?? ''}
          subtitle={t('organizationsPage.users')}
          backBtn={{ isVisible: true, onClick: () => history.push('/') }}
        >
          <AppButton
            variant='transparent'
            disabled={usersStore.isLoadingUsers}
            data-test='add-player-button'
            onClick={() => {
              usersStore.displayUsersDialog(true);
            }}
          >
            {t('organizationsPage.addUser')}
            <AppUseIcon name='PlusIconWithoutBorder' width='14' height='14' className='ml-2' />
          </AppButton>
        </AppPageHeader>

        <TableWithFilters<SingleUserType>
          appTableFiltersInstance={appTableFiltersInstance}
          rowMapper={rowMapper}
          columns={columns}
          data={rows}
          isLoading={usersStore.isLoadingUsers}
        />

        <AddUserDialog
          organizationId={organizationId}
          isOpened={usersStore.isUsersDialogOpened}
          user={usersStore.selectedUser}
          onClose={() => {
            usersStore.displayUsersDialog(false);
            setTimeout(() => {
              usersStore.isSearchingForNewUser = false;
              usersStore.setSelectedUser(null);
            }, 500);
          }}
        />

        <AppConfirmDialog
          isDialogOpen={isConfirmRevokeDialogOpen}
          onConfirm={() => {
            handleRevokingUserAccess(userToRevoke);
            setIsConfirmRevokeDialogOpen(false);
            setUserToRevoke(null);
          }}
          onCancel={() => {
            setIsConfirmRevokeDialogOpen(false);
            setUserToRevoke(null);
          }}
          title={t('usersPage.revokeUser')}
          message={t('usersPage.revokeUserConfirmation')}
        />
      </div>
    </AppSectionErrorBoundary>
  );
});

export default UsersPage;
